"use client";

import { fetchWithAuth } from "@/lib/api-helpers";

/**
 * Client for /api/synced-databases — the MANAGEMENT surface (list,
 * refresh, stop) for databases that mirror a remote project.
 *
 * Deliberately its own file rather than more exports on
 * exportImportService: creation lives in the import flow, management
 * lives in settings, and keeping them apart also keeps this PR from
 * touching the same files as the creation PR (closot-frontend#277) —
 * the two land independently, in either order.
 */

export interface SyncedDatabaseSummary {
  id: string;
  connectorId: "jira" | "gitlab" | "asana";
  remoteName: string;
  dataSourceId: string;
  pageId: string;
  lastSyncedAt?: string;
  /**
   * 'partial' is a real state, not a failure: a run stopped at a safety
   * cap. lastRunMessage carries the server's own explanation and the UI
   * must show it rather than collapse this into "Synced".
   */
  lastRunStatus?: "ok" | "partial" | "error";
  lastRunMessage?: string;
  truncated?: boolean;
}

export interface SyncRunSummary {
  created: number;
  updated: number;
  truncated: boolean;
  status: "ok" | "partial" | "error";
  message?: string;
}

export async function listSyncedDatabases(
  workspaceId: string,
): Promise<{ syncs: SyncedDatabaseSummary[]; supportedConnectors: string[] } | null> {
  try {
    const response = await fetchWithAuth(
      `/api/synced-databases?workspaceId=${encodeURIComponent(workspaceId)}`,
      { headers: { "x-workspace-id": workspaceId } },
    );
    if (!response.ok) return null;
    return response.json();
  } catch {
    return null;
  }
}

export async function refreshSyncedDatabase(
  syncId: string,
  workspaceId: string,
): Promise<SyncRunSummary | null> {
  try {
    const response = await fetchWithAuth(
      `/api/synced-databases/${encodeURIComponent(syncId)}/refresh`,
      { method: "POST", headers: { "x-workspace-id": workspaceId } },
    );
    if (!response.ok) return null;
    return response.json();
  } catch {
    return null;
  }
}

/**
 * Stops the sync. The database and its rows are KEPT — the server never
 * deletes user content on unsync, and no caller may describe this as
 * deleting anything.
 */
export async function removeSyncedDatabase(
  syncId: string,
  workspaceId: string,
): Promise<boolean> {
  try {
    const response = await fetchWithAuth(
      `/api/synced-databases/${encodeURIComponent(syncId)}`,
      { method: "DELETE", headers: { "x-workspace-id": workspaceId } },
    );
    return response.ok;
  } catch {
    return false;
  }
}
